import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, X, BarChart3 } from "lucide-react";

const MAX_OPTIONS = 10;
const MIN_OPTIONS = 2;

// Client-side unique id generator for poll option rows so React keys stay
// stable across add/remove (index-as-key breaks focus & input state when
// rows are removed from the middle of the list).
let __optSeq = 0;
const makeOpt = (text = "") => ({ id: `opt_${++__optSeq}`, text });

/**
 * WhatsApp-group-style poll composer. Emits `{question, options, allow_multi}`
 * via `onSubmit` so the parent can post a channel message with a `poll` field.
 */
export default function PollComposer({ open, onOpenChange, onSubmit }) {
  const [question, setQuestion] = useState("");
  const initial = useRef(null);
  if (initial.current === null) initial.current = [makeOpt(), makeOpt()];
  const [options, setOptions] = useState(initial.current);
  const [allowMulti, setAllowMulti] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setQuestion("");
    setOptions([makeOpt(), makeOpt()]);
    setAllowMulti(false);
  };

  const close = () => {
    if (busy) return;
    reset();
    onOpenChange(false);
  };

  const setOpt = (id, v) => setOptions((cur) => cur.map((o) => (o.id === id ? { ...o, text: v } : o)));
  const addOpt = () => {
    if (options.length >= MAX_OPTIONS) return;
    setOptions((cur) => [...cur, makeOpt()]);
  };
  const removeOpt = (id) => {
    if (options.length <= MIN_OPTIONS) return;
    setOptions((cur) => cur.filter((o) => o.id !== id));
  };

  const submit = async () => {
    const q = question.trim();
    const cleanOpts = options.map((o) => o.text.trim()).filter(Boolean);
    if (!q) { toast.error("Add a question"); return; }
    if (cleanOpts.length < 2) { toast.error("Add at least 2 options"); return; }
    setBusy(true);
    try {
      await onSubmit({
        question: q,
        options: cleanOpts.map((text) => ({ text })),
        allow_multi: allowMulti,
      });
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create poll");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? close() : onOpenChange(v))}>
      <DialogContent className="sm:max-w-md" data-testid="poll-composer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[var(--brand-accent)]"/> Create poll
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Question</label>
            <Input
              autoFocus
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="What do you want to ask?"
              data-testid="poll-question"
              maxLength={200}
            />
          </div>
          <div className="space-y-2">
            <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Options</label>
            {options.map((opt, i) => (
              <div key={opt.id} className="flex items-center gap-2">
                <Input
                  value={opt.text}
                  onChange={(e) => setOpt(opt.id, e.target.value)}
                  placeholder={`Option ${i + 1}`}
                  data-testid={`poll-option-${i}`}
                  maxLength={80}
                />
                <button
                  onClick={() => removeOpt(opt.id)}
                  disabled={options.length <= MIN_OPTIONS}
                  className="p-1.5 rounded hover:bg-zinc-100 text-zinc-400 hover:text-red-600 disabled:opacity-30 disabled:pointer-events-none"
                  aria-label="Remove option"
                  data-testid={`poll-option-remove-${i}`}
                >
                  <X className="w-4 h-4"/>
                </button>
              </div>
            ))}
            {options.length < MAX_OPTIONS && (
              <button
                onClick={addOpt}
                className="text-xs text-[var(--brand-accent)] font-medium flex items-center gap-1 hover:underline"
                data-testid="poll-add-option"
              >
                <Plus className="w-3 h-3"/> Add option
              </button>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={allowMulti}
              onChange={(e) => setAllowMulti(e.target.checked)}
              data-testid="poll-allow-multi"
            />
            <span>Allow multiple selections</span>
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={close} disabled={busy}>Cancel</Button>
            <Button
              size="sm"
              onClick={submit}
              disabled={busy}
              className="bg-[var(--brand-accent)] text-white hover:bg-orange-700"
              data-testid="poll-submit"
            >
              {busy ? "Posting…" : "Post poll"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
